import type {
	ShipmentCheckCall,
	ShipmentRemark,
} from "~/shared/schemas/shipment-management-schema";
import { parseServerDateTime } from "~/shared/utils/date-formatter.ts";

export const groupRemarksOrCheckCallsByDate = <
	T extends ShipmentRemark | ShipmentCheckCall,
>(
	items: T[],
): Record<string, T[]> => {
	// Remarks carry a local `dateEntered`; check-call timestamps are UTC instants
	// (LTL Internal Dispatch sends an explicit offset, others omit it), so parse
	// those as UTC and convert to the viewer's local time. This keeps grouping and
	// newest-to-oldest ordering consistent across all shipment types.
	const getItemDate = (item: T): Date =>
		"dateEntered" in item
			? new Date(item.dateEntered)
			: parseServerDateTime(item.timestamp);

	const groups: Record<string, T[]> = {};
	for (const item of items) {
		const date = getItemDate(item).toLocaleDateString("en-US", {
			weekday: "long",
			month: "long",
			day: "numeric",
			year: "numeric",
		});
		if (groups[date]) {
			groups[date].push(item);
		} else {
			groups[date] = [item];
		}
	}

	for (const date in groups) {
		groups[date]?.sort(
			(a, b) => getItemDate(b).getTime() - getItemDate(a).getTime(),
		);
	}

	// Re-insert keys sorted newest-to-oldest so consumers iterating the
	// record (e.g. via Object.keys / Object.entries) render dates descending.
	const sortedGroups: Record<string, T[]> = {};
	const sortedDateKeys = Object.keys(groups).sort((a, b) => {
		const aItems = groups[a];
		const bItems = groups[b];
		const aLatest = aItems?.[0] ? getItemDate(aItems[0]).getTime() : 0;
		const bLatest = bItems?.[0] ? getItemDate(bItems[0]).getTime() : 0;
		return bLatest - aLatest;
	});
	for (const date of sortedDateKeys) {
		const items = groups[date];
		if (items) sortedGroups[date] = items;
	}

	return sortedGroups;
};
